// ============================================
// TEEMPAD EVENTS - Type Definitions
// ============================================

// ============================================
// MULTITENANT MODEL
// ============================================

export interface User {
  id: string
  email: string
  displayName: string
  photoUrl?: string
  defaultOrgId?: string
  isSuperAdmin?: boolean
  createdAt: number
  lastLoginAt: number
}

/** Organization: club, federation, or event organizer */
export interface Organization {
  id: string
  name: string
  shortName: string
  logoUrl?: string
  color: string
  ownerId: string
  /** If set, this org is a Club (has ClubMembers) */
  isClub?: boolean
  subscription?: Subscription
  createdAt: number
}

/** Role within an Organization */
export type OrgRole = 'admin' | 'staff' | 'member' | 'viewer'

export const ORG_ROLE_LABELS: Record<OrgRole, string> = {
  admin: 'Administrador',
  staff: 'Staff',
  member: 'Miembro',
  viewer: 'Lector'
}

export const ORG_ROLE_PERMISSIONS: Record<OrgRole, string[]> = {
  admin: ['*'],
  staff: ['events:read', 'events:write', 'tasks:read', 'tasks:write', 'activities:join'],
  member: ['events:read', 'events:participate', 'tasks:read', 'activities:join'],
  viewer: ['events:read']
}

/** Member of an Organization */
export interface OrgMember {
  id: string
  orgId: string
  userId: string
  role: OrgRole
  joinedAt: number
}

/** ClubMember: only when Organization.isClub is true (for club-internal events) */
export interface ClubMember {
  id: string
  clubId: string
  userId: string
  role: OrgRole
  joinedAt: number
}

// ============================================
// EVENTS
// ============================================

export type EventType = 'tournament' | 'club_internal' | 'sponsored' | 'exhibition'

export type EventStatus = 'draft' | 'published' | 'ongoing' | 'completed' | 'cancelled'

export const EVENT_STATUS_LABELS: Record<EventStatus, string> = {
  draft: 'Borrador',
  published: 'Publicado',
  ongoing: 'En curso',
  completed: 'Finalizado',
  cancelled: 'Cancelado'
}

export const EVENT_TYPE_LABELS: Record<EventType, string> = {
  tournament: 'Torneo',
  club_internal: 'Interno',
  sponsored: 'Patrocinado',
  exhibition: 'Exhibición'
}

/** Colores por tipo de evento - Rams: restrained, functional */
export const EVENT_TYPE_COLORS: Record<EventType, { from: string; to: string }> = {
  tournament: { from: '#1c1917', to: '#44403c' },
  club_internal: { from: '#44403c', to: '#57534e' },
  sponsored: { from: '#b45309', to: '#92400e' },
  exhibition: { from: '#57534e', to: '#78716c' }
}

export interface Event {
  id: string
  orgId: string
  name: string
  slug: string
  description?: string
  type: EventType
  status: EventStatus
  startDate: string
  endDate: string
  timezone: string
  /** Main venue/location */
  primaryVenueId?: string
  primaryLocation?: string
  imageUrl?: string
  contactEmail?: string
  contactPhone?: string
  rulesUrl?: string
  createdAt: number
  updatedAt: number
  createdBy: string
}

/** Participant in an event (staff, delegate, etc.) */
export interface EventMember {
  id: string
  eventId: string
  orgId: string
  userId: string
  role: 'admin' | 'organizer' | 'member' | 'viewer'
  joinedAt: number
}

/** Entity (club, school, association) registered in an event */
export interface EventEntity {
  id: string
  eventId: string
  orgId: string
  name: string
  shortName?: string
  logoUrl?: string
  contactName?: string
  contactEmail?: string
  contactPhone?: string
  participantCount: number
  staffCount?: number
  teamCount?: number
  status: 'pending' | 'confirmed' | 'paid'
  registeredAt: number
  notes?: string
}

/** Competition team within an entity */
export interface EventTeam {
  id: string
  eventId: string
  orgId: string
  entityId: string
  name: string
  shortName?: string
  category?: string
  participantCount: number
  createdAt: number
}

// ============================================
// KANBAN & TASKS (per event)
// ============================================

export type TaskPriority = 'low' | 'medium' | 'high'

export const TASK_PRIORITY_LABELS: Record<TaskPriority, string> = {
  low: 'Baja',
  medium: 'Media',
  high: 'Alta'
}

export interface KanbanColumn {
  id: string
  eventId: string
  orgId: string
  name: string
  order: number
  color?: string
}

export interface Subtask {
  id: string
  title: string
  done: boolean
}

export interface EventTask {
  id: string
  eventId: string
  orgId: string
  columnId: string
  title: string
  description?: string
  subtasks?: Subtask[]
  assigneeIds: string[]
  dueDate?: string
  priority: TaskPriority
  order: number
  createdBy: string
  createdAt: number
  updatedAt: number
}

export interface TaskTemplate {
  title: string
  priority: TaskPriority
  subtasks?: Omit<Subtask, 'id'>[]
}

export const EVENT_TASK_TEMPLATES: Record<EventType, TaskTemplate[]> = {
  tournament: [
    { title: 'Definir formato de competición (llaves, grupos)', priority: 'high' },
    { title: 'Gestionar inscripciones de equipos', priority: 'high', subtasks: [
      { title: 'Crear formulario de inscripción', done: false },
      { title: 'Confirmar equipos inscritos', done: false },
      { title: 'Comunicar plazos de inscripción', done: false }
    ]},
    { title: 'Contratar y coordinar arbitraje', priority: 'high' },
    { title: 'Preparar sedes y pistas', priority: 'medium', subtasks: [
      { title: 'Reservar instalaciones', done: false },
      { title: 'Verificar estado de pistas', done: false },
      { title: 'Señalización y cartelería', done: false }
    ]},
    { title: 'Organizar premios y trofeos', priority: 'medium' },
    { title: 'Planificar catering / avituallamiento', priority: 'medium', subtasks: [
      { title: 'Pedir presupuestos', done: false },
      { title: 'Confirmar menú y cantidades', done: false },
      { title: 'Coordinar horario de servicio', done: false }
    ]},
    { title: 'Preparar material de comunicación', priority: 'low', subtasks: [
      { title: 'Diseñar cartel del evento', done: false },
      { title: 'Publicar en redes sociales', done: false },
      { title: 'Enviar comunicación a participantes', done: false }
    ]},
    { title: 'Coordinar voluntarios', priority: 'medium' },
    { title: 'Plan de seguridad y emergencias', priority: 'low' },
    { title: 'Preparar acreditaciones', priority: 'low' }
  ],
  club_internal: [
    { title: 'Definir actividades del evento', priority: 'high' },
    { title: 'Comunicar a los socios', priority: 'high' },
    { title: 'Reservar espacio / instalaciones', priority: 'medium' },
    { title: 'Organizar material necesario', priority: 'medium' },
    { title: 'Preparar refrigerio', priority: 'low' },
    { title: 'Documentación fotográfica', priority: 'low' }
  ],
  sponsored: [
    { title: 'Cerrar acuerdo con patrocinador', priority: 'high', subtasks: [
      { title: 'Presentar propuesta de patrocinio', done: false },
      { title: 'Negociar condiciones', done: false },
      { title: 'Firmar contrato', done: false }
    ]},
    { title: 'Integrar branding del patrocinador', priority: 'high', subtasks: [
      { title: 'Recibir logos y materiales', done: false },
      { title: 'Aplicar branding en comunicaciones', done: false },
      { title: 'Preparar banners y lonas', done: false }
    ]},
    { title: 'Gestionar inscripciones', priority: 'high' },
    { title: 'Coordinar logística del evento', priority: 'medium' },
    { title: 'Preparar informe de visibilidad para patrocinador', priority: 'medium' },
    { title: 'Cobertura en redes sociales', priority: 'medium' },
    { title: 'Preparar regalos / merchandising', priority: 'low' }
  ],
  exhibition: [
    { title: 'Definir programa de exhibiciones', priority: 'high' },
    { title: 'Confirmar participantes / artistas', priority: 'high' },
    { title: 'Preparar espacio de exhibición', priority: 'medium' },
    { title: 'Equipo de sonido e iluminación', priority: 'medium' },
    { title: 'Comunicación y promoción', priority: 'medium' },
    { title: 'Gestionar accesos del público', priority: 'low' }
  ]
}

// ============================================
// VENUES & POIs
// ============================================

/** Tipo de venue oficial: sede de competición o parking */
export type VenueType = 'venue' | 'parking'

export const VENUE_TYPE_LABELS: Record<VenueType, string> = {
  venue: 'Sede / Pabellón',
  parking: 'Parking'
}

export interface Venue {
  id: string
  eventId: string
  orgId: string
  name: string
  /** venue = sede/pabellón, parking = aparcamiento recomendado */
  venueType?: VenueType
  address?: string
  lat?: number
  lng?: number
  capacity?: number
  notes?: string
  createdAt: number
}

export type POICategory = 'restaurant' | 'accommodation' | 'pharmacy' | 'transport' | 'leisure' | 'other'

export const POI_CATEGORY_LABELS: Record<POICategory, string> = {
  restaurant: 'Restaurante',
  accommodation: 'Alojamiento',
  pharmacy: 'Farmacia',
  transport: 'Transporte',
  leisure: 'Ocio (playa, museo, etc.)',
  other: 'Otro'
}

export interface POI {
  id: string
  eventId: string
  orgId: string
  name: string
  category: POICategory
  address?: string
  lat?: number
  lng?: number
  phone?: string
  url?: string
  description?: string
  createdAt: number
}

// ============================================
// ACTIVITIES & SCHEDULE
// ============================================

export interface Activity {
  id: string
  eventId: string
  orgId: string
  title: string
  description?: string
  date: string
  startTime: string
  endTime?: string
  venueId?: string
  maxParticipants?: number
  participantIds: string[]
  createdAt: number
  updatedAt: number
}

export type ScheduleItemType = 'match' | 'activity' | 'ceremony' | 'other'
export type ScheduleItemStatus = 'scheduled' | 'live' | 'finished' | 'cancelled'

export const SCHEDULE_TYPE_LABELS: Record<ScheduleItemType, string> = {
  match: 'Partido',
  activity: 'Actividad',
  ceremony: 'Ceremonia',
  other: 'Otro'
}

export const SCHEDULE_STATUS_LABELS: Record<ScheduleItemStatus, string> = {
  scheduled: 'Programado',
  live: 'En juego',
  finished: 'Finalizado',
  cancelled: 'Cancelado'
}

export interface ScheduleItem {
  id: string
  eventId: string
  orgId: string
  type: ScheduleItemType
  title: string
  date: string
  startTime: string
  endTime?: string
  venueId?: string
  teamHomeId?: string
  teamAwayId?: string
  homeScore?: number
  awayScore?: number
  status: ScheduleItemStatus
  groupId?: string
  round?: number
  createdAt: number
}

// ============================================
// TEAM PARTICIPANTS (roster per team)
// ============================================

export interface TeamParticipant {
  id: string
  eventId: string
  orgId: string
  teamId: string
  name: string
  number?: number
  position?: string
  isStaff?: boolean
  createdAt: number
}

export const ENTITY_STATUS_LABELS: Record<EventEntity['status'], string> = {
  pending: 'Pendiente',
  confirmed: 'Confirmado',
  paid: 'Pagado'
}

// ============================================
// TOURNAMENT CONFIG & GROUPS
// ============================================

export type TournamentFormat = 'groups' | 'knockout' | 'groups_knockout' | 'league'

export const TOURNAMENT_FORMAT_LABELS: Record<TournamentFormat, string> = {
  groups: 'Fase de grupos',
  knockout: 'Eliminatoria directa',
  groups_knockout: 'Grupos + Eliminatoria',
  league: 'Liga (todos contra todos)'
}

export interface TournamentConfig {
  id: string
  eventId: string
  orgId: string
  format: TournamentFormat
  groupCount: number
  pointsWin: number
  pointsDraw: number
  pointsLoss: number
  createdAt: number
  updatedAt: number
}

export interface TournamentGroup {
  id: string
  eventId: string
  orgId: string
  name: string
  order: number
  teamIds: string[]
}

export interface GroupStanding {
  teamId: string
  teamName: string
  played: number
  won: number
  drawn: number
  lost: number
  goalsFor: number
  goalsAgainst: number
  goalDifference: number
  points: number
}

// ============================================
// ANNOUNCEMENTS
// ============================================

export type AnnouncementPriority = 'info' | 'warning' | 'urgent'

export const ANNOUNCEMENT_PRIORITY_LABELS: Record<AnnouncementPriority, string> = {
  info: 'Información',
  warning: 'Aviso',
  urgent: 'Urgente'
}

export interface Announcement {
  id: string
  eventId: string
  orgId: string
  title: string
  body: string
  priority: AnnouncementPriority
  pinned?: boolean
  createdBy: string
  createdAt: number
}

// ============================================
// BUDGET & EXPENSES
// ============================================

export type BudgetItemType = 'income' | 'expense'
export type BudgetCategory =
  | 'venue' | 'catering' | 'transport' | 'equipment' | 'prizes'
  | 'marketing' | 'staff' | 'insurance' | 'registration_income'
  | 'sponsor_income' | 'other_income' | 'other'

export const BUDGET_CATEGORY_LABELS: Record<BudgetCategory, string> = {
  venue: 'Instalaciones',
  catering: 'Catering',
  transport: 'Transporte',
  equipment: 'Material',
  prizes: 'Premios / Trofeos',
  marketing: 'Marketing',
  staff: 'Personal',
  insurance: 'Seguros',
  registration_income: 'Inscripciones',
  sponsor_income: 'Patrocinios',
  other_income: 'Otros ingresos',
  other: 'Otros gastos'
}

export const EXPENSE_CATEGORIES: BudgetCategory[] = ['venue', 'catering', 'transport', 'equipment', 'prizes', 'marketing', 'staff', 'insurance', 'other']
export const INCOME_CATEGORIES: BudgetCategory[] = ['registration_income', 'sponsor_income', 'other_income']

export interface BudgetItem {
  id: string
  eventId: string
  orgId: string
  type: BudgetItemType
  category: BudgetCategory
  description: string
  amount: number
  date?: string
  paid?: boolean
  notes?: string
  createdBy: string
  createdAt: number
  updatedAt: number
}

// ============================================
// DOCUMENTS & FILES
// ============================================

export type DocumentType = 'rules' | 'waiver' | 'insurance' | 'contract' | 'other'

export const DOCUMENT_TYPE_LABELS: Record<DocumentType, string> = {
  rules: 'Reglamento',
  waiver: 'Consentimiento',
  insurance: 'Seguro',
  contract: 'Contrato',
  other: 'Otro'
}

export interface EventDocument {
  id: string
  eventId: string
  orgId: string
  name: string
  url: string
  docType: DocumentType
  size?: number
  createdBy: string
  createdAt: number
}

// ============================================
// QR ACCREDITATION & CHECK-IN
// ============================================

export type AccreditationType = 'entity' | 'participant' | 'staff' | 'vip' | 'media'

export const ACCREDITATION_TYPE_LABELS: Record<AccreditationType, string> = {
  entity: 'Entidad',
  participant: 'Participante',
  staff: 'Staff',
  vip: 'VIP',
  media: 'Prensa'
}

export interface Accreditation {
  id: string
  eventId: string
  orgId: string
  type: AccreditationType
  name: string
  entityId?: string
  teamId?: string
  code: string
  checkedIn?: boolean
  checkedInAt?: number
  checkedInBy?: string
  zone?: string
  createdAt: number
}

// ============================================
// SPONSORS
// ============================================

export type SponsorStatus = 'lead' | 'contacted' | 'negotiating' | 'confirmed' | 'active' | 'completed'

export const SPONSOR_STATUS_LABELS: Record<SponsorStatus, string> = {
  lead: 'Potencial',
  contacted: 'Contactado',
  negotiating: 'Negociando',
  confirmed: 'Confirmado',
  active: 'Activo',
  completed: 'Completado'
}

export const SPONSOR_STATUS_COLORS: Record<SponsorStatus, string> = {
  lead: 'var(--color-text-muted)',
  contacted: 'var(--color-info)',
  negotiating: 'var(--color-warning)',
  confirmed: 'var(--color-accent)',
  active: 'var(--color-success)',
  completed: 'var(--color-text-secondary)'
}

export interface SponsorTier {
  id: string
  eventId: string
  orgId: string
  name: string
  order: number
  price?: number
  maxSponsors?: number
  benefits: string[]
  logoSize?: 'sm' | 'md' | 'lg' | 'xl'
  createdAt: number
}

export interface EventSponsor {
  id: string
  eventId: string
  orgId: string
  tierId: string
  name: string
  logoUrl?: string
  website?: string
  contactName?: string
  contactEmail?: string
  contactPhone?: string
  notes?: string
  status: SponsorStatus
  createdAt: number
  updatedAt?: number
}

export interface SponsorDeliverable {
  id: string
  eventId: string
  orgId: string
  sponsorId: string
  label: string
  delivered: boolean
  deliveredAt?: number
  deliveredBy?: string
  createdAt: number
}

// ============================================
// SPONSOR DOSSIER (Phase 2)
// ============================================

export type DossierSection =
  | 'event_info'
  | 'venue_map'
  | 'audience'
  | 'sponsor_tiers'
  | 'confirmed_sponsors'
  | 'schedule_preview'
  | 'custom'

export const DOSSIER_SECTION_LABELS: Record<DossierSection, string> = {
  event_info: 'Información del evento',
  venue_map: 'Sedes y mapa',
  audience: 'Audiencia esperada',
  sponsor_tiers: 'Tiers de patrocinio',
  confirmed_sponsors: 'Patrocinadores confirmados',
  schedule_preview: 'Programa del evento',
  custom: 'Sección personalizada'
}

export interface SponsorDossier {
  id: string
  eventId: string
  orgId: string
  title: string
  subtitle?: string
  sections: DossierSection[]
  customIntro?: string
  customClosing?: string
  expectedAudience?: number
  expectedReach?: number
  contactName?: string
  contactEmail?: string
  contactPhone?: string
  accentColor?: string
  token: string
  viewCount: number
  lastViewedAt?: number
  createdAt: number
  updatedAt?: number
}

export interface DossierView {
  id: string
  dossierId: string
  eventId: string
  orgId: string
  viewedAt: number
  referrer?: string
}

// ============================================
// SPONSOR PORTAL (Phase 3)
// ============================================

export interface SponsorAccessToken {
  id: string
  eventId: string
  orgId: string
  sponsorId: string
  token: string
  createdAt: number
  expiresAt?: number
}

export type SponsorMediaType = 'photo' | 'article' | 'social_post' | 'video'

export const SPONSOR_MEDIA_TYPE_LABELS: Record<SponsorMediaType, string> = {
  photo: 'Foto',
  article: 'Artículo',
  social_post: 'Publicación social',
  video: 'Vídeo'
}

export interface SponsorMedia {
  id: string
  eventId: string
  orgId: string
  sponsorId: string
  type: SponsorMediaType
  url: string
  thumbnailUrl?: string
  title?: string
  description?: string
  source?: string
  publishedAt?: number
  createdAt: number
}

// ============================================
// ENROLLMENT (self-registration by external clubs)
// ============================================

export type EnrollmentStatus = 'pending' | 'approved' | 'rejected'

export type CustomFieldType = 'text' | 'textarea' | 'number' | 'checkbox' | 'select' | 'multiselect'

export interface CustomField {
  id: string
  type: CustomFieldType
  label: string
  required: boolean
  placeholder?: string
  options?: string[]            // for select / multiselect
  order: number
}

export interface CustomFieldValue {
  fieldId: string
  value: string | string[] | boolean | number
}

export interface EnrollmentConfig {
  id: string
  eventId: string
  orgId: string
  token: string
  slug?: string
  status: 'open' | 'closed'
  deadline?: string
  maxEnrollments?: number
  instructions?: string
  requirePlayerDetails: boolean
  availableCategories: string[]
  customFields?: CustomField[]
  enrollmentCount?: number
  createdAt: number
  updatedAt: number
}

export interface EnrollmentPlayer {
  name: string
  number?: number
  position?: string
}

export interface EnrollmentTeam {
  name: string
  category: string
  expectedPlayerCount: number
  players: EnrollmentPlayer[]
}

export interface Enrollment {
  id: string
  eventId: string
  orgId: string
  configId: string
  entityName: string
  entityShortName?: string
  entityLogoUrl?: string
  contactName: string
  contactRole: string
  contactEmail: string
  contactPhone: string
  teams: EnrollmentTeam[]
  expectedStaffCount?: number
  customFieldValues?: CustomFieldValue[]
  status: EnrollmentStatus
  reviewedBy?: string
  reviewedAt?: number
  rejectionReason?: string
  notes?: string
  submittedBy?: string
  approvedEntityId?: string
  submittedAt: number
}

// ============================================
// SUBSCRIPTION & TIERS
// ============================================

export type SubscriptionTier = 'free' | 'pro' | 'business'
export type SubscriptionStatus = 'active' | 'past_due' | 'cancelled' | 'incomplete'
export type BillingInterval = 'month' | 'year'

export interface Subscription {
  tier: SubscriptionTier
  status: SubscriptionStatus
  billingInterval?: BillingInterval
  stripeCustomerId?: string
  stripeSubscriptionId?: string
  currentPeriodEnd?: string
  cancelAtPeriodEnd?: boolean
}

export interface TierLimits {
  maxActiveEvents: number        // -1 = unlimited
  maxOrgMembers: number          // -1 = unlimited
  maxParticipantsPerEvent: number // -1 = unlimited
  maxVenuesPerEvent: number      // -1 = unlimited
  features: TierFeature[]
}

export type TierFeature =
  | 'public_event'
  | 'basic_tasks'
  | 'tasks_full'
  | 'task_templates'
  | 'weather'
  | 'branding'
  | 'export_csv'
  | 'export_api'
  | 'priority_support'
  | 'budget'
  | 'documents'
  | 'accreditation'
  | 'tv_mode'
  | 'ai_schedule'
  | 'report_generator'
  | 'announcements'
  | 'sponsors'
  | 'sponsors_full'
  | 'enrollment'

export const TIER_LIMITS: Record<SubscriptionTier, TierLimits> = {
  free: {
    maxActiveEvents: 1,
    maxOrgMembers: 3,
    maxParticipantsPerEvent: 50,
    maxVenuesPerEvent: 2,
    features: ['public_event', 'basic_tasks', 'announcements']
  },
  pro: {
    maxActiveEvents: 10,
    maxOrgMembers: 15,
    maxParticipantsPerEvent: 500,
    maxVenuesPerEvent: 10,
    features: ['public_event', 'tasks_full', 'task_templates', 'weather', 'branding', 'export_csv', 'budget', 'documents', 'announcements', 'tv_mode', 'sponsors', 'enrollment']
  },
  business: {
    maxActiveEvents: -1,
    maxOrgMembers: -1,
    maxParticipantsPerEvent: -1,
    maxVenuesPerEvent: -1,
    features: ['public_event', 'tasks_full', 'task_templates', 'weather', 'branding', 'export_csv', 'export_api', 'priority_support', 'budget', 'documents', 'accreditation', 'tv_mode', 'ai_schedule', 'report_generator', 'announcements', 'sponsors', 'sponsors_full', 'enrollment']
  }
}

export const TIER_LABELS: Record<SubscriptionTier, string> = {
  free: 'Free',
  pro: 'Pro',
  business: 'Business'
}

export const TIER_PRICES: Record<SubscriptionTier, { month: number; year: number }> = {
  free: { month: 0, year: 0 },
  pro: { month: 15, year: 150 },
  business: { month: 49, year: 490 }
}

export const FEATURE_LABELS: Record<TierFeature, string> = {
  public_event: 'Evento público',
  basic_tasks: 'Tareas básicas',
  tasks_full: 'Gestión de tareas completa',
  task_templates: 'Plantillas de tareas',
  weather: 'Previsión meteorológica',
  branding: 'Logo y colores personalizados',
  export_csv: 'Exportar datos (CSV)',
  export_api: 'Acceso API',
  priority_support: 'Soporte prioritario',
  budget: 'Control de presupuesto',
  documents: 'Documentos adjuntos',
  accreditation: 'Acreditaciones QR',
  tv_mode: 'Pantalla TV en directo',
  ai_schedule: 'Generador IA de horarios',
  report_generator: 'Informes post-evento',
  announcements: 'Anuncios y avisos',
  sponsors: 'Gestión de patrocinadores',
  sponsors_full: 'Patrocinadores: tiers, entregables y portal',
  enrollment: 'Inscripciones online'
}

/** Default subscription for new organizations */
export const DEFAULT_SUBSCRIPTION: Subscription = {
  tier: 'free',
  status: 'active'
}

// ============================================
// ENTITY DELEGATES (participant auth linking)
// ============================================

/** Links an authenticated user to an EventEntity */
export interface EntityDelegate {
  id: string
  eventId: string
  orgId: string
  entityId: string
  userId: string
  role: 'contact' | 'delegate'
  createdAt: number
}

// ============================================
// EVENT NOTIFICATIONS
// ============================================

export type NotificationChannel = 'in_app' | 'push' | 'email'

export interface EventNotification {
  id: string
  eventId: string
  orgId: string
  title: string
  body: string
  priority: AnnouncementPriority
  targetEntityIds?: string[]
  channels: NotificationChannel[]
  createdBy: string
  createdAt: number
}

/** Per-user read receipt for notification feed */
export interface NotificationReceipt {
  id: string
  notificationId: string
  eventId: string
  orgId: string
  userId: string
  read: boolean
  readAt?: number
  createdAt: number
}

/** FCM token per user/device */
export interface UserFcmToken {
  id: string
  userId: string
  token: string
  createdAt: number
  updatedAt: number
}
